import type { CSSProperties } from 'react';
import { fmtNumber, fmtTimestamp, fmtTokens, fmtUsd } from '@/lib/format';
import type { RequestLogRow } from '@/types';

/**
 * "Recent requests" card shared by My usage and the admin Usage page.
 * `showCaller` adds the caller name / user UPN columns for admins.
 */
export function RecentRequestsTable({ rows, isLoading, error, showCaller = false, style }: {
  rows: RequestLogRow[];
  isLoading: boolean;
  error: Error | null;
  showCaller?: boolean;
  style?: CSSProperties;
}) {
  const colCount = showCaller ? 10 : 8;

  return (
    <div className="card" style={style}>
      <div className="card-header"><h3>Recent requests</h3></div>
      {error ? (
        <div className="card-body">
          <div className="error-banner" style={{ marginBottom: 0 }}>
            Could not load recent requests: {error.message}
          </div>
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Time</th>
              {showCaller && <th>Caller</th>}
              {showCaller && <th>User</th>}
              <th>Model</th>
              <th className="num">Tokens in</th>
              <th className="num">Tokens out</th>
              <th className="num">Cost</th>
              <th className="num">Latency</th>
              <th>Status</th>
              <th>Cache</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={colCount} className="placeholder">Loading recent requests...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={colCount} className="placeholder">No requests logged yet.</td></tr>
            ) : rows.map((r, i) => (
              <tr key={`${r.correlationId}-${i}`}>
                <td style={{ whiteSpace: 'nowrap' }}>{fmtTimestamp(r.timestampUtc)}</td>
                {showCaller && <td><span className="row-title">{r.callerName}</span></td>}
                {showCaller && <td>{r.userUpn ?? '—'}</td>}
                <td><span className="row-title">{r.supplier}/{r.modelId}</span></td>
                <td className="num">{fmtTokens(r.inputTokens)}</td>
                <td className="num">{fmtTokens(r.outputTokens)}</td>
                <td className="num">{fmtUsd(r.estimatedCostUsd)}</td>
                <td className="num">{fmtNumber(r.latencyMs)} ms</td>
                <td style={{ whiteSpace: 'nowrap' }} title={r.correlationId}>
                  <span style={{ color: r.success ? 'var(--kt-green)' : 'var(--kt-red)', fontWeight: 700 }}>
                    {r.success ? '✓' : '✗'}
                  </span>
                  {r.finishReason && (
                    <span style={{ marginLeft: 6, fontSize: '.82rem', color: 'var(--kt-grey-500)' }}>
                      {r.finishReason}
                    </span>
                  )}
                </td>
                <td>{r.servedFromCache && <span className="pill pill-success">cache</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
